import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardShell from '@/components/DashboardShell';
import api, { getImageUrl, PLACEHOLDER_IMAGE } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Send, ShieldAlert, MessageCircle, Image as ImageIcon, Loader2, ChevronUp, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

export default function Enquiries() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [typingUser, setTypingUser] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const wsRef = useRef(null);
  const msgsEndRef = useRef(null);
  const msgsContainerRef = useRef(null);
  const typingTimer = useRef(null);
  const reconnectRef = useRef({ attempts: 0, timer: null, manual: false });
  const activeConvRef = useRef(null);
  const lastMsgIdRef = useRef(null);
  const PAGE_SIZE = 30;

  // ── Load conversations ────────────────────────────────────────────
  const loadConversations = useCallback(async () => {
    try {
      const r = await api.get('/conversations');
      setConversations(r.data);
    } catch {
      toast.error('Failed to load conversations');
    } finally {
      setLoadingConvs(false);
    }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  // ── Re-fetch latest page and merge (used after reconnect) ──
  const fetchLatest = useCallback(async (convId) => {
    try {
      const r = await api.get(`/conversations/${convId}/messages`, { params: { limit: PAGE_SIZE } });
      const incoming = r.data.messages || [];
      setMessages(prev => {
        const byId = new Map(prev.map(m => [m.id, m]));
        incoming.forEach(m => byId.set(m.id, m));
        return Array.from(byId.values()).sort((a, b) => (a.timestamp || '').localeCompare(b.timestamp || ''));
      });
    } catch { /* ignore */ }
  }, []);

  // ── WebSocket connector with auto-reconnect (exponential backoff) ──
  const connectWS = useCallback((convId) => {
    const token = localStorage.getItem('tlr_token');
    if (!token) return;
    const base = (process.env.REACT_APP_BACKEND_URL || '')
      .replace(/^https:/, 'wss:')
      .replace(/^http:/, 'ws:');
    const wsUrl = `${base}/api/ws/conversations/${convId}?token=${token}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setWsConnected(true);
      setReconnecting(false);
      if (reconnectRef.current.attempts > 0) {
        // recovered from a drop — pull any messages missed while offline
        fetchLatest(convId);
      }
      reconnectRef.current.attempts = 0;
    };

    ws.onclose = () => {
      setWsConnected(false);
      wsRef.current = null;
      // Only reconnect if the drop was unexpected and we're still on this conversation
      if (!reconnectRef.current.manual && activeConvRef.current === convId) {
        const attempt = reconnectRef.current.attempts + 1;
        if (attempt <= 6) {
          reconnectRef.current.attempts = attempt;
          const delay = Math.min(1000 * 2 ** (attempt - 1), 15000);
          setReconnecting(true);
          clearTimeout(reconnectRef.current.timer);
          reconnectRef.current.timer = setTimeout(() => {
            if (activeConvRef.current === convId) connectWS(convId);
          }, delay);
        } else {
          setReconnecting(false);
        }
      }
    };

    ws.onerror = () => {};

    ws.onmessage = (e) => {
      try {
        const { type, data } = JSON.parse(e.data);
        if (type === 'message') {
          setMessages(prev => {
            if (prev.find(m => m.id === data.id)) return prev;
            return [...prev, data];
          });
          setConversations(prev => prev.map(c =>
            c.id === convId ? { ...c, last_message: data.content, last_message_at: data.timestamp } : c
          ));
          setTypingUser(null);
        } else if (type === 'typing') {
          if (data.user_id !== user?.id) {
            setTypingUser(data.user_name);
            clearTimeout(typingTimer.current);
            typingTimer.current = setTimeout(() => setTypingUser(null), 3000);
          }
        } else if (type === 'error') {
          toast.error(data.message);
        }
      } catch {}
    };
  }, [user?.id, fetchLatest]);

  // ── Open conversation ─────────────────────────────────────────────
  const openConversation = useCallback(async (convId) => {
    if (activeConvId === convId) return;
    // Tear down existing socket manually (don't trigger reconnect)
    reconnectRef.current.manual = true;
    clearTimeout(reconnectRef.current.timer);
    reconnectRef.current.attempts = 0;
    if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    setReconnecting(false);
    activeConvRef.current = convId;
    setActiveConvId(convId);
    setMessages([]);
    setHasMore(false);
    lastMsgIdRef.current = null;
    setLoadingMsgs(true);
    try {
      const [msgsR] = await Promise.all([
        api.get(`/conversations/${convId}/messages`, { params: { limit: PAGE_SIZE } }),
        api.get(`/conversations/${convId}`),
      ]);
      setMessages(msgsR.data.messages || []);
      setHasMore(!!msgsR.data.has_more);
      setConversations(prev => prev.map(c =>
        c.id === convId
          ? { ...c, unread_buyer: user?.id === c.buyer_id ? 0 : c.unread_buyer, unread_seller: user?.id === c.seller_id ? 0 : c.unread_seller }
          : c
      ));
    } catch {
      toast.error('Failed to load messages');
    } finally {
      setLoadingMsgs(false);
    }
    reconnectRef.current.manual = false;
    connectWS(convId);
  }, [activeConvId, user, connectWS]);

  // ── Load older messages (pagination, preserves scroll position) ──
  const loadOlder = async () => {
    if (!activeConvId || loadingOlder || messages.length === 0) return;
    setLoadingOlder(true);
    const container = msgsContainerRef.current;
    const prevHeight = container ? container.scrollHeight : 0;
    try {
      const before = messages[0].timestamp;
      const r = await api.get(`/conversations/${activeConvId}/messages`, { params: { before, limit: PAGE_SIZE } });
      const older = r.data.messages || [];
      setMessages(prev => {
        const existing = new Set(prev.map(m => m.id));
        return [...older.filter(m => !existing.has(m.id)), ...prev];
      });
      setHasMore(!!r.data.has_more);
      requestAnimationFrame(() => {
        if (container) container.scrollTop = container.scrollHeight - prevHeight;
      });
    } catch {
      toast.error('Failed to load older messages');
    } finally {
      setLoadingOlder(false);
    }
  };

  // Auto-open conversation from URL ?conv=xxx (placed AFTER openConversation is defined)
  useEffect(() => {
    const convId = searchParams.get('conv');
    if (convId && conversations.length > 0) {
      const found = conversations.find(c => c.id === convId);
      if (found) openConversation(convId);
    }
  }, [conversations, searchParams, openConversation]);

  // Cleanup WS on unmount
  useEffect(() => () => {
    reconnectRef.current.manual = true;
    clearTimeout(reconnectRef.current.timer);
    if (wsRef.current) wsRef.current.close();
  }, []);

  // Scroll to bottom only when a NEW message arrives at the end (not when prepending older)
  useEffect(() => {
    const lastId = messages[messages.length - 1]?.id;
    if (lastId && lastId !== lastMsgIdRef.current) {
      lastMsgIdRef.current = lastId;
      msgsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (typingUser) msgsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [typingUser]);

  // ── Send message via WS (with REST fallback) ──────────────────────
  const sendMessage = async () => {
    const content = draft.trim();
    if (!content || !activeConvId) return;
    setSending(true);
    setDraft('');

    // Try WS first
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'message', content }));
      setSending(false);
      return;
    }

    // REST fallback
    try {
      const r = await api.post(`/conversations/${activeConvId}/messages`, { content });
      setMessages(prev => [...prev, r.data]);
      setConversations(prev => prev.map(c =>
        c.id === activeConvId ? { ...c, last_message: content, last_message_at: r.data.timestamp } : c
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send message');
      setDraft(content); // restore
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
    // Send typing indicator
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing' }));
    }
  };

  const activeConv = conversations.find(c => c.id === activeConvId);
  const otherParty = (conv) => {
    if (!conv || !user) return '';
    return user.id === conv.buyer_id ? conv.seller_name : conv.buyer_name;
  };
  const myUnread = (conv) => {
    if (!conv || !user) return 0;
    return user.id === conv.buyer_id ? (conv.unread_buyer || 0) : (conv.unread_seller || 0);
  };

  return (
    <DashboardShell title="Enquiries">
      <div
        className="flex rounded-2xl overflow-hidden border"
        style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)', height: 'calc(100vh - 160px)', minHeight: '520px' }}
        data-testid="enquiries-container"
      >
        {/* ── Left: Conversation list ── */}
        <div
          className="flex-shrink-0 border-r flex flex-col"
          style={{ width: '300px', borderColor: 'var(--border-clr)', backgroundColor: 'var(--bg)' }}
        >
          <div className="px-4 py-4 border-b" style={{ borderColor: 'var(--border-clr)' }}>
            <h2 className="font-semibold text-base" style={{ fontFamily: 'var(--font-serif)', color: 'var(--text)' }}>Conversations</h2>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{conversations.length} thread{conversations.length !== 1 ? 's' : ''}</p>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loadingConvs ? (
              <div className="p-4 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}</div>
            ) : conversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                <MessageCircle size={28} className="mb-3" style={{ color: 'var(--text-muted)' }} />
                <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>No conversations yet</p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Enquire on a listing to start</p>
              </div>
            ) : (
              conversations.map(conv => {
                const unread = myUnread(conv);
                const isActive = conv.id === activeConvId;
                const thumb = conv.listing_image ? getImageUrl(conv.listing_image) : PLACEHOLDER_IMAGE;
                return (
                  <button
                    key={conv.id}
                    onClick={() => openConversation(conv.id)}
                    className="w-full text-left px-4 py-3 flex items-center gap-3 transition-colors hover:bg-[var(--bg-subtle)]"
                    style={{
                      backgroundColor: isActive ? 'var(--bg-subtle)' : 'transparent',
                      borderLeft: isActive ? '3px solid var(--gold)' : '3px solid transparent',
                    }}
                    data-testid={`conv-item-${conv.id}`}
                  >
                    <img src={thumb} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" style={{ backgroundColor: 'var(--bg-subtle)' }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1">
                        <p className="text-xs font-semibold truncate" style={{ color: 'var(--text)' }}>{otherParty(conv)}</p>
                        {unread > 0 && (
                          <span className="flex-shrink-0 w-4 h-4 rounded-full text-white text-[10px] flex items-center justify-center" style={{ backgroundColor: 'var(--gold)' }}>{unread}</span>
                        )}
                      </div>
                      <p className="text-xs truncate mt-0.5" style={{ color: 'var(--text-muted)' }}>{conv.listing_title}</p>
                      <p className="text-xs truncate mt-0.5" style={{ color: 'var(--text-muted)', opacity: 0.7 }}>{conv.last_message || '...'}</p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* ── Right: Message thread ── */}
        <div className="flex-1 flex flex-col min-w-0">
          {!activeConvId ? (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
              <MessageCircle size={40} className="mb-4" style={{ color: 'var(--text-muted)', opacity: 0.4 }} />
              <p className="text-lg font-medium" style={{ fontFamily: 'var(--font-serif)', color: 'var(--text)' }}>Select a conversation</p>
              <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Choose a thread from the left to start chatting</p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="flex items-center gap-3 px-5 py-4 border-b" style={{ borderColor: 'var(--border-clr)' }}>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-sm" style={{ color: 'var(--text)' }}>{otherParty(activeConv)}</p>
                    {reconnecting ? (
                      <span className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--warning)' }} data-testid="ws-reconnecting">
                        <RefreshCw size={10} className="animate-spin" /> Reconnecting…
                      </span>
                    ) : wsConnected ? (
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--success)' }} title="Connected" data-testid="ws-connected" />
                    ) : (
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--text-muted)' }} title="Offline" />
                    )}
                  </div>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    Re: <span style={{ fontStyle: 'italic' }}>{activeConv?.listing_title}</span>
                  </p>
                </div>
              </div>

              {/* Messages */}
              <div ref={msgsContainerRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                {hasMore && !loadingMsgs && (
                  <div className="flex justify-center pb-1">
                    <button
                      onClick={loadOlder}
                      disabled={loadingOlder}
                      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition-colors hover:bg-[var(--bg-subtle)]"
                      style={{ color: 'var(--text-muted)', border: '1px solid var(--border-clr)' }}
                      data-testid="load-older-button"
                    >
                      {loadingOlder ? <Loader2 size={12} className="animate-spin" /> : <ChevronUp size={12} />}
                      {loadingOlder ? 'Loading…' : 'Load older messages'}
                    </button>
                  </div>
                )}
                {loadingMsgs ? (
                  <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-10 w-3/4 rounded-xl" style={{ marginLeft: i%2===0 ? 'auto' : '' }} />)}</div>
                ) : messages.map(msg => {
                  const isMine = msg.sender_id === user?.id;
                  const isAdmin = msg.is_admin_message;
                  return (
                    <div key={msg.id} className={`flex flex-col ${isMine ? 'items-end' : 'items-start'}`} data-testid="chat-message">
                      {isAdmin && (
                        <div className="flex items-center gap-1 mb-1">
                          <ShieldAlert size={11} style={{ color: 'var(--gold)' }} />
                          <span className="text-[10px] font-medium" style={{ color: 'var(--gold)' }}>Admin · {msg.sender_name}</span>
                        </div>
                      )}
                      {!isMine && !isAdmin && (
                        <p className="text-[10px] mb-1" style={{ color: 'var(--text-muted)' }}>{msg.sender_name}</p>
                      )}
                      <div
                        className="max-w-[72%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed"
                        style={{
                          backgroundColor: isAdmin
                            ? 'rgba(28,28,28,0.12)'
                            : isMine ? 'var(--gold)' : 'var(--bg-subtle)',
                          color: isMine && !isAdmin ? 'white' : 'var(--text)',
                          borderRadius: isMine ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                          border: isAdmin ? '1px solid var(--gold)' : 'none',
                        }}
                      >
                        {msg.content}
                      </div>
                      <p className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>{formatTime(msg.timestamp)}</p>
                    </div>
                  );
                })}

                {/* Typing indicator */}
                {typingUser && (
                  <div className="flex items-start">
                    <div className="flex items-center gap-1 px-4 py-2.5 rounded-2xl" style={{ backgroundColor: 'var(--bg-subtle)', borderRadius: '18px 18px 18px 4px' }}>
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{typingUser} is typing</span>
                      <span className="flex gap-0.5 ml-1">
                        {[0,1,2].map(i => (
                          <span key={i} className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ backgroundColor: 'var(--text-muted)', animationDelay: `${i * 0.15}s` }} />
                        ))}
                      </span>
                    </div>
                  </div>
                )}

                <div ref={msgsEndRef} />
              </div>

              {/* Input */}
              <div className="px-5 py-4 border-t" style={{ borderColor: 'var(--border-clr)' }}>
                <div className="flex items-end gap-3">
                  <Textarea
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type a message… (Enter to send, Shift+Enter for new line)"
                    rows={1}
                    className="resize-none text-sm flex-1"
                    style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--bg-subtle)', maxHeight: '120px' }}
                    data-testid="message-input"
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={sending || !draft.trim()}
                    size="icon"
                    className="flex-shrink-0"
                    style={{ backgroundColor: 'var(--gold)', color: 'white', borderRadius: '12px', width: '40px', height: '40px' }}
                    data-testid="send-message-button"
                  >
                    <Send size={16} />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}
